// Import react
import React, { useState } from "react";

// Import global hooks
import { useToast } from "global/hooks/useToast";

// Import components
import CreateButton from "global/components/CreateButton";
import ContextMenuButton from "global/components/ContextMenuButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import RenderColourwayPopup from "./ColourwayPopUp";



// Render custom product colourway details
const RenderColourwayDetails = ({
    selectedCustomProduct,
    setSelectedCustomProduct,
    addingNewCustomProduct,
    addNewCustomProductColourway,
    updateExistingColourway,
    deleteColourway
}) => {

    // Define toast options
    const { toastError } = useToast();

    // Store the colourway currently being updated so the toggle can be disabled while the API call runs
    const [updatingColourwayCode, setUpdatingColourwayCode] = useState(null);

    // Store whether the add/edit colourway popup is currently open
    const [colourwayPopupIsOpen, setColourwayPopupIsOpen] = useState(false);

    // Store the colourway currently being edited
    const [selectedColourway, setSelectedColourway] = useState(null);

    // Store the colourway currently being dragged for ordering changes
    const [draggedColourwayCode, setDraggedColourwayCode] = useState(null);

    // Store the colourway currently receiving a dragged item
    const [dragOverColourwayCode, setDragOverColourwayCode] = useState(null);

    // Get the colourways from the selected custom product
    const colourways = selectedCustomProduct?.colourways || [];

    // Sort the colourways by ordering so they appear in the intended order
    const sortedColourways = [...colourways].sort((a, b) => {

        // Convert the first ordering value to a number
        const firstOrdering = Number(a.ordering || 0);

        // Convert the second ordering value to a number
        const secondOrdering = Number(b.ordering || 0);

        // Return the sort comparison
        return firstOrdering - secondOrdering;

    });

    // Open the popup for adding a new colourway
    const handleOpenAddColourway = () => {

        // Clear any selected colourway so the popup knows this is an add
        setSelectedColourway(null);

        // Open the colourway popup
        setColourwayPopupIsOpen(true);

    };

    // Open the popup for editing an existing colourway
    const handleOpenEditColourway = (colourway) => {

        // Store the selected colourway so the popup can populate the form
        setSelectedColourway(colourway);

        // Open the colourway popup
        setColourwayPopupIsOpen(true);

    };

    // Close the add/edit colourway popup
    const handleCloseColourwayPopup = () => {

        // Close the colourway popup
        setColourwayPopupIsOpen(false);

        // Clear the selected colourway
        setSelectedColourway(null);

    };

    // Save the colourway from the popup
    const handleSaveColourway = async (colourwayData) => {

        // Store the API response
        let response;

        // If a colourway is selected, update the existing colourway
        if (selectedColourway) {

            // Update the existing colourway using the original code in the query string
            response = await updateExistingColourway(
                selectedCustomProduct.code,
                selectedColourway.code,
                colourwayData,
                setSelectedCustomProduct
            );

        } else {

            // Add the new colourway to the selected custom product
            response = await addNewCustomProductColourway(
                selectedCustomProduct.code,
                colourwayData,
                setSelectedCustomProduct
            );

        }

        // If the save worked, close the popup and show a success message
        if (response?.success) {

            // Close the popup
            handleCloseColourwayPopup();

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not save colourway: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });

        }

    };

    // Permanently delete a colourway
    const handleDeleteColourway = async (colourwayToDelete = null) => {

        // Use the passed colourway first, otherwise use the colourway currently open in the popup
        const targetColourway = colourwayToDelete || selectedColourway;

        // If no colourway is available, stop before making the API call
        if (!targetColourway) return;

        // Confirm the user really wants to permanently delete this colourway
        const confirmed = window.confirm("Permanently delete colourway " + targetColourway.code + "? This cannot be undone.");

        // If the delete was not confirmed, stop here
        if (!confirmed) return;

        // Delete the colourway using the selected product code and target colourway code
        const response = await deleteColourway(
            selectedCustomProduct.code,
            targetColourway.code,
            setSelectedCustomProduct
        );

        // If the delete worked, close the popup and show a success message
        if (response?.success) {

            // Close the popup if this delete was triggered from the edit popup
            if (selectedColourway && selectedColourway.code === targetColourway.code) handleCloseColourwayPopup();

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not delete colourway: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });

        }

    };

    // Start dragging a colourway row for ordering
    const handleColourwayDragStart = (colourway, event) => {

        // Store the dragged colourway code
        setDraggedColourwayCode(colourway.code);

        // Store the dragged colourway code in the native drag event
        event.dataTransfer.setData("text/plain", colourway.code);

        // Use move mode for this drag action
        event.dataTransfer.effectAllowed = "move";

    };

    // Track which row the dragged colourway is currently over
    const handleColourwayDragOver = (colourway, event) => {

        // Prevent the browser blocking the drop action
        event.preventDefault();

        // If the dragged row is over itself, clear the drop highlight
        if (draggedColourwayCode === colourway.code) {

            // Clear the drop target
            setDragOverColourwayCode(null);

            // Stop here
            return;

        }

        // Store the current drop target
        setDragOverColourwayCode(colourway.code);

    };

    // Clear drag hover state when leaving a row
    const handleColourwayDragLeave = () => {

        // Clear the current drop target
        setDragOverColourwayCode(null);

    };

    // Drop one colourway onto another row and ask the API to adjust ordering
    const handleColourwayDrop = async (targetColourway, event) => {

        // Prevent the browser's default drop behaviour
        event.preventDefault();

        // Get the dragged code from state first
        let droppedColourwayCode = draggedColourwayCode;

        // If state did not contain the code, try the native drag event
        if (!droppedColourwayCode) droppedColourwayCode = event.dataTransfer.getData("text/plain");

        // Clear drag state now the drop is complete
        setDraggedColourwayCode(null);

        // Clear the drop target state
        setDragOverColourwayCode(null);

        // If we have no dragged colourway code, stop here
        if (!droppedColourwayCode) return;

        // If the colourway was dropped on itself, stop here
        if (droppedColourwayCode === targetColourway.code) return;

        // Find the dragged colourway from the current colourways
        const droppedColourway = sortedColourways.find((colourway) => colourway.code === droppedColourwayCode);

        // If the dragged colourway cannot be found, stop here
        if (!droppedColourway) return;

        // Ask the API to move the dragged colourway to the target ordering position
        const response = await updateExistingColourway(
            selectedCustomProduct.code,
            droppedColourway.code,
            { ordering: Number(targetColourway.ordering) },
            setSelectedCustomProduct
        );

        // Show an error only when the order update failed
        if (!response?.success) {
            toastError({ title: "Error", message: "Could not update colourway order: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });
        }

    };

    // Clear drag state after dragging ends
    const handleColourwayDragEnd = () => {

        // Clear the dragged colourway code
        setDraggedColourwayCode(null);

        // Clear the drop target state
        setDragOverColourwayCode(null);

    };

    // Handle switching a colourway between active and inactive
    const handleToggleColourwayActive = async (colourway, event) => {

        // Get the boolean value from the toggle switch
        const isActive = event.target.value;

        // Convert the boolean toggle value into the API's Y/N format
        const activeValue = isActive ? "Y" : "N";

        // Store the code so this toggle can be disabled while saving
        setUpdatingColourwayCode(colourway.code);

        // Update only the active value for this colourway
        const response = await updateExistingColourway(
            selectedCustomProduct.code,
            colourway.code,
            { active: activeValue },
            setSelectedCustomProduct
        );

        // Clear the updating state now the API call has completed
        setUpdatingColourwayCode(null);

        // Show an error only when the update failed
        if (!response?.success) {
            toastError({ title: "Error", message: "Could not update colourway: " + (response?.error_message || "Unknown error"), autoCloseMs: 6000 });
        }

    };

    // If this is a new product, colourways cannot be managed yet because the product code does not exist in the API
    if (addingNewCustomProduct) {

        return (

            <div className="colourways-list-empty">

                <p>You need to save the product before adding colourways.</p>

            </div>

        );

    }

    // Render the colourway list
    return (

        <div className="colourways-list-container">

            <div className="colourways-list-toolbar">

                <CreateButton
                    title="+ Add Colourway"
                    size="s"
                    onClick={handleOpenAddColourway}
                />

            </div>

            {sortedColourways.length > 0 ?

                <div className="colourways-list">

                    <div className="colourways-list__header">

                        <div className="colourways-list__cell colourways-list__cell--actions"></div>

                        <div className="colourways-list__cell">Ordering</div>

                        <div className="colourways-list__cell">Colour</div>

                        <div className="colourways-list__cell">Code</div>

                        <div className="colourways-list__cell">Stock Code</div>

                        <div className="colourways-list__cell">Core Stock</div>

                        <div className="colourways-list__cell">Stock Used</div>

                        <div className="colourways-list__cell">Name</div>

                        <div className="colourways-list__cell">Hex Colour</div>

                        <div className="colourways-list__cell">Active</div>

                    </div>

                    {sortedColourways.map((colourway, index) => (

                    <div
                            key={colourway.code || index}
                            className={"colourways-list__row" + (dragOverColourwayCode === colourway.code ? " colourways-list__row--drag-over" : "")}
                            draggable={true}
                            onDragStart={(event) => handleColourwayDragStart(colourway, event)}
                            onDragOver={(event) => handleColourwayDragOver(colourway, event)}
                            onDragLeave={handleColourwayDragLeave}
                            onDrop={(event) => handleColourwayDrop(colourway, event)}
                            onDragEnd={handleColourwayDragEnd}
                        >

                            <div className="colourways-list__cell colourways-list__cell--actions">

                                <ContextMenuButton
                                    buttonTitle="&hellip;"
                                    /*
                                    menuOptions={[
                                        {
                                            label: "Edit",
                                            clickFunction: () => handleOpenEditColourway(colourway)
                                        },
                                        {
                                            label: "Delete",
                                            color: "red",
                                            clickFunction: () => handleDeleteColourway(colourway)
                                        }
                                    ]}
                                    */
                                    selectedIDs={colourway.code}
                                    selectedDetails={colourway}
                                    disabled={false}
                                    itemIsActive={colourway.active}
                                    singleOnClickFunction={() => handleOpenEditColourway(colourway)}
                                />

                            </div>

                            <div className="colourways-list__cell">{colourway.ordering}</div>

                            <div className="colourways-list__cell">

                                <span
                                    className="colourways-list__swatch"
                                    style={{ backgroundColor: colourway.hex_colour || "transparent" }}
                                    title={colourway.hex_colour || ""}
                                ></span>

                            </div>

                            <div className="colourways-list__cell">{colourway.code}</div>

                            <div className="colourways-list__cell">{colourway.stock_code}</div>

                            <div className="colourways-list__cell" style={{textAlign: "center"}}>{colourway.core}</div>

                            <div className="colourways-list__cell">{colourway.stock_used}</div>

                            <div className="colourways-list__cell">{colourway.name}</div>

                            <div className="colourways-list__cell">{colourway.hex_colour}</div>

                            <div className="colourways-list__cell">

                                <ToggleSwitch
                                    name={"colourway_active_" + colourway.code}
                                    value={colourway.active === "Y"}
                                    onChange={(event) => handleToggleColourwayActive(colourway, event)}
                                    label=""
                                    labelPosition="right"
                                    disabled={updatingColourwayCode === colourway.code}
                                    size="s"
                                />

                            </div>

                        </div>

                    ))}

                </div>

                :

                <div className="colourways-list-empty">

                    <p>No colourways have been added yet.</p>

                </div>

            }

            <RenderColourwayPopup
                isOpen={colourwayPopupIsOpen}
                onClose={handleCloseColourwayPopup}
                colourway={selectedColourway}
                handleSaveColourway={handleSaveColourway}
                handleDeleteColourway={handleDeleteColourway}
            />

        </div>

    );

};

export default RenderColourwayDetails;